import * as React from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { MapPin, ChevronDown, Building2, Check } from "lucide-react";
import { useLingui } from "@lingui/react/macro";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/useAuth";
import { useActiveOrganization } from "@/contexts/ActiveOrganizationContext";
import { useActiveLocation } from "@/contexts/ActiveLocationContext";
import type { Location } from "@/types/location";

function VirtualLocationList({
  locations,
  activeLocation,
  onSelect,
}: {
  locations: Location[];
  activeLocation: Location | null;
  onSelect: (loc: Location | null) => void;
}) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: locations.length,
    getScrollElement: () => containerRef.current,
    estimateSize: () => 32,
    overscan: 8,
  });

  if (locations.length === 0) return null;

  return (
    <div ref={containerRef} className="max-h-52 overflow-y-auto">
      <div style={{ height: virtualizer.getTotalSize(), position: "relative" }}>
        {virtualizer.getVirtualItems().map((item) => {
          const loc = locations[item.index];
          if (!loc) return null;
          return (
            <DropdownMenuItem
              key={loc.id}
              onClick={() => onSelect(loc)}
              className="absolute inset-x-0 flex items-center gap-2"
              style={{ top: item.start, height: item.size }}
            >
              <span className="size-3.5 shrink-0 flex items-center justify-center">
                {activeLocation?.id === loc.id && (
                  <Check className="size-3.5 text-(--color-action)" />
                )}
              </span>
              {loc.name}
            </DropdownMenuItem>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Active-location picker. Shared by the desktop TopBar and the page headers
 * (e.g. Orders) so they all switch the active location rather than just
 * displaying it. The trigger styling is supplied per context via
 * `triggerClassName`; the dropdown body is identical everywhere.
 */
export function LocationSelector({
  triggerClassName,
  align = "start",
}: {
  triggerClassName?: string;
  align?: "start" | "end";
}) {
  const { t } = useLingui();
  const { user, hasPermission, hasRole } = useAuth();
  const { activeOrganization } = useActiveOrganization();
  const { activeLocation, setActiveLocation, locations } = useActiveLocation();
  const label = activeLocation?.name ?? t`All locations`;

  const isSuperAdmin = hasPermission("*:*") || hasRole("SUPER_ADMIN");
  const orgName = isSuperAdmin
    ? activeOrganization?.name
    : (user?.organization?.name ?? user?.company?.name);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t`Switch workspace location`}
          className={cn("inline-flex items-center gap-2", triggerClassName)}
        >
          <MapPin className="size-4 shrink-0 text-(--color-text-secondary)" />
          <span className="max-w-32 truncate">{label}</span>
          <ChevronDown className="size-3.5 shrink-0 text-(--color-text-secondary)" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="min-w-48">
        {orgName && (
          <>
            <DropdownMenuLabel className="flex items-center gap-1.5 text-xs text-(--color-text-secondary) font-normal">
              <Building2 className="size-3.5 shrink-0" />
              {orgName}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuItem onClick={() => setActiveLocation(null)}>{t`All locations`}</DropdownMenuItem>
        <VirtualLocationList
          locations={locations}
          activeLocation={activeLocation}
          onSelect={setActiveLocation}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
